import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'

export type ToastVariant = 'info' | 'success' | 'warning' | 'error'

export type Toast = {
  id: string
  variant: ToastVariant
  message: string
  duration: number
}

type ToastContextValue = {
  toasts: Toast[]
  push: (message: string, opts?: { variant?: ToastVariant; duration?: number }) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const VARIANT_CLASS: Record<ToastVariant, string> = {
  info: 'bg-gray-900 text-white',
  success: 'bg-green-600 text-white',
  warning: 'bg-amber-500 text-white',
  error: 'bg-red-600 text-white',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const counter = useRef(0)

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback(
    (message: string, opts?: { variant?: ToastVariant; duration?: number }) => {
      counter.current += 1
      const id = `t${counter.current}`
      const variant = opts?.variant ?? 'info'
      const duration = opts?.duration ?? 3500
      setToasts((prev) => [...prev, { id, message, variant, duration }])
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration)
      }
      return id
    },
    [dismiss],
  )

  return (
    <ToastContext.Provider value={{ toasts, push, dismiss }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onDismiss(t.id)}
          className={cn(
            'pointer-events-auto max-w-md rounded-lg px-4 py-2 text-sm shadow-lg transition-opacity',
            VARIANT_CLASS[t.variant],
          )}
        >
          {t.message}
        </button>
      ))}
    </div>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // 在没有 Provider 时返回 noop（方便组件单独渲染于 storybook 等）
    return {
      push: (_msg: string) => '',
      dismiss: (_id: string) => undefined,
      toasts: [] as Toast[],
    }
  }
  return ctx
}

// 便捷快捷 hook：同步触发后副作用清理
export function useToastEffect(message: string | null, variant: ToastVariant = 'info') {
  const { push } = useToast()
  useEffect(() => {
    if (message) push(message, { variant })
  }, [message, variant, push])
}
